import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode, RefObject, SyntheticEvent } from 'react'
import TableRow, { type TableRowProps } from '@mui/material/TableRow'
import type { MenuProps } from '@mui/material/Menu'
import type { PopoverPosition } from '@mui/material/Popover'

type OpenContextMenu = (position: PopoverPosition) => void
const ActionMenuContext = createContext<RefObject<OpenContextMenu | null> | null>(null)

type ActionMenuTargetProps = Pick<HTMLAttributes<HTMLElement>, 'onContextMenu' | 'onKeyDown' | 'tabIndex'>

/**
 * Connects a resource surface to its actions without adding a DOM wrapper.
 * Render one primary action menu inside each scope; shared table rows already provide a scope.
 */
export const ActionMenuScope = ({
    onContextMenu,
    onKeyDown,
    tabIndex = -1,
    children,
}: ActionMenuTargetProps & {
    children: (props: ActionMenuTargetProps) => ReactNode
}) => {
    const openMenu = useRef<OpenContextMenu | null>(null)

    const open = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
        const target = event.target
        if (
            event.defaultPrevented ||
            !openMenu.current ||
            !(target instanceof Element) ||
            !event.currentTarget.contains(target) ||
            target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')
        ) {
            return
        }

        const keyboard = !('clientX' in event) || (event.clientX === 0 && event.clientY === 0)
        const bounds = target.getBoundingClientRect()
        event.preventDefault()
        event.stopPropagation()
        if (!keyboard) event.currentTarget.focus({ preventScroll: true })
        openMenu.current(
            keyboard ? { left: bounds.left, top: bounds.bottom } : { left: event.clientX, top: event.clientY }
        )
    }

    return (
        <ActionMenuContext.Provider value={openMenu}>
            {children({
                tabIndex,
                onContextMenu: (event) => {
                    onContextMenu?.(event)
                    // Shift-right-click provides the browser menu, including on resource links.
                    if (!event.shiftKey) open(event)
                },
                onKeyDown: (event) => {
                    onKeyDown?.(event)
                    if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) open(event)
                },
            })}
        </ActionMenuContext.Provider>
    )
}

/** A table row that exposes its existing resource actions on right-click. */
export const ActionMenuRow = ({ onContextMenu, onKeyDown, tabIndex, ...props }: TableRowProps) => (
    <ActionMenuScope onContextMenu={onContextMenu} onKeyDown={onKeyDown} tabIndex={tabIndex}>
        {(targetProps) => <TableRow {...props} {...targetProps} />}
    </ActionMenuScope>
)

type MenuState = { open: boolean } & ({ element: HTMLElement } | { position: PopoverPosition })

/**
 * Stops a primary press that dismisses a backdrop-less context menu from also acting on what lies beneath,
 * such as a dialog's backdrop, a row, or another control, matching the backdrop of a button-opened menu.
 */
const absorbPress = (event: Event, document: Document) => {
    // Cancelling pointerdown also suppresses the compatibility mousedown, but not the click.
    event.preventDefault()
    event.stopPropagation()
    const release = new AbortController()
    const options = { capture: true, signal: release.signal }
    document.addEventListener(
        'click',
        (click) => {
            click.preventDefault()
            click.stopPropagation()
            release.abort()
        },
        options
    )
    // A press that never becomes a click, such as a drag or a release outside the window, must not swallow a later one.
    document.addEventListener('pointerdown', () => release.abort(), options)
    document.addEventListener('pointercancel', () => release.abort(), options)
    document.addEventListener('pointerup', () => setTimeout(() => release.abort()), options)
}

/** Shares one menu between its button and its resource row, preserving the anchor while it closes. */
export const useActionMenu = (available = true) => {
    const rowMenu = useContext(ActionMenuContext)
    const [state, setState] = useState<MenuState | null>(null)
    const [menuElement, setMenuElement] = useState<HTMLDivElement | null>(null)
    const contextMenu = state !== null && 'position' in state
    const open = available && !!state?.open

    const handleClose = (event?: Pick<SyntheticEvent, 'stopPropagation'>) => {
        event?.stopPropagation()
        setState((current) => (current ? { ...current, open: false } : null))
    }

    useLayoutEffect(() => {
        if (!available) {
            setState((current) => (current?.open ? { ...current, open: false } : current))
            return
        }
        if (!rowMenu) return
        const openAt: OpenContextMenu = (position) => setState({ open: true, position })
        rowMenu.current = openAt
        return () => {
            if (rowMenu.current === openAt) rowMenu.current = null
        }
    }, [available, rowMenu])

    useEffect(() => {
        if (!open || !contextMenu || !menuElement) return
        const document = menuElement.ownerDocument

        const dismissOutside = (event: Event) => {
            if (!(event.target instanceof Node) || menuElement.contains(event.target)) return
            setState((current) => (current ? { ...current, open: false } : null))
            // A secondary press (or Ctrl-click, the macOS secondary click) goes on to retarget another resource.
            const { button, ctrlKey } = event as PointerEvent
            if (event.type === 'pointerdown' && button === 0 && !ctrlKey) absorbPress(event, document)
        }
        // Only the open context menu listens. Capture lets another row open its menu in the same gesture.
        document.addEventListener('pointerdown', dismissOutside, true)
        document.addEventListener('contextmenu', dismissOutside, true)
        document.addEventListener('scroll', dismissOutside, true)
        return () => {
            document.removeEventListener('pointerdown', dismissOutside, true)
            document.removeEventListener('contextmenu', dismissOutside, true)
            document.removeEventListener('scroll', dismissOutside, true)
        }
    }, [contextMenu, open, menuElement])

    const menuProps: MenuProps = {
        ref: setMenuElement,
        open,
        anchorEl: state && 'element' in state ? state.element : null,
        anchorReference: contextMenu ? 'anchorPosition' : 'anchorEl',
        anchorPosition: contextMenu ? state.position : undefined,
        onClose: () => handleClose(),
        onClick: (event) => event.stopPropagation(),
        onContextMenu: (event) => event.stopPropagation(),
        // Let a second right-click reach another resource instead of the modal backdrop.
        hideBackdrop: contextMenu,
        disableEnforceFocus: contextMenu,
        disableScrollLock: contextMenu,
        sx: contextMenu ? { pointerEvents: 'none', '& .MuiPaper-root': { pointerEvents: 'auto' } } : undefined,
    }

    return {
        open,
        menuProps,
        handleClose,
        handleClick: (event: MouseEvent<HTMLElement>) => {
            event.stopPropagation()
            if (available) setState({ open: true, element: event.currentTarget })
        },
    }
}
